// src/components/product/ProductBasicInfoSection.tsx

import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, Stack, TextField, Typography } from '@mui/material';

interface ProductBasicInfoSectionProps {
  /** Immutable product identifier shown to the user. */
  productCode: string;
  /** Editable display name. */
  productName: string;
  /** Editable long description. */
  description: string;
  /** Sales price (USD). */
  price: number;

  /** Propagate product name changes to the parent form state. */
  onChangeName: (value: string) => void;
  /** Propagate description changes to the parent form state. */
  onChangeDescription: (value: string) => void;
  /** Propagate normalized numeric price to the parent form state. */
  onChangePrice: (value: number) => void;
}

/**
 * ProductBasicInfoSection
 *
 * Displays and edits the core product fields:
 * - Product code (read-only)
 * - Product name
 * - Description
 * - Price (USD)
 *
 * Price input is controlled as a string to allow natural typing (e.g. "12.", "0.1").
 * The parent form receives a normalized numeric value only on blur:
 * - Empty input is normalized to 0.00
 * - Values are formatted to 2 decimal places
 */
const ProductBasicInfoSection = ({
  productCode,
  productName,
  description,
  price,
  onChangeName,
  onChangeDescription,
  onChangePrice,
}: ProductBasicInfoSectionProps) => {
  // Keep a string state for smooth typing; normalize to a number on blur only.
  const [priceInput, setPriceInput] = useState(() =>
    Number.isFinite(price) ? price.toFixed(2) : '',
  );

  // Sync local input when the parent value changes externally (e.g., initial load/reset).
  useEffect(() => {
    setPriceInput(Number.isFinite(price) ? price.toFixed(2) : '');
  }, [price]);

  const handlePriceChange = (raw: string) => {
    // Allow empty while editing (will be normalized on blur).
    if (raw === '') {
      setPriceInput('');
      return;
    }

    // Allow digits and an optional decimal part with up to 2 digits.
    // Examples: "12", "12.", "12.3", "12.34"
    if (!/^\d*\.?\d{0,2}$/.test(raw)) return;

    setPriceInput(raw);
  };

  const handlePriceBlur = () => {
    // Normalize empty input to 0.00.
    if (priceInput === '') {
      setPriceInput('0.00');
      onChangePrice(0);
      return;
    }

    const numeric = Number(priceInput);
    const normalized = Number.isNaN(numeric) ? 0 : numeric;
    const formatted = normalized.toFixed(2);

    setPriceInput(formatted);
    onChangePrice(Number(formatted));
  };

  return (
    <Card
      elevation={0}
      sx={(theme) => ({
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        mb: 3,
      })}
    >
      <CardHeader
        title={
          <Typography variant="h6" fontWeight={700}>
            General
          </Typography>
        }
        subheader={
          <Typography variant="body2" color="text.secondary">
            Basic product details such as code, name, description and price.
          </Typography>
        }
      />

      <CardContent>
        <Stack spacing={2}>
          <TextField
            label="Product Code"
            value={productCode}
            InputProps={{ readOnly: true }}
            size="small"
            sx={{ maxWidth: 360 }}
          />

          <TextField
            label="Product Name"
            placeholder="Enter product name"
            size="small"
            value={productName}
            onChange={(e) => onChangeName(e.target.value)}
          />

          <TextField
            label="Description"
            placeholder="Describe the product"
            size="small"
            value={description || ''}
            onChange={(e) => onChangeDescription(e.target.value)}
            multiline
            minRows={4}
          />

          <TextField
            label="Price"
            size="small"
            type="text"
            value={priceInput}
            onChange={(e) => handlePriceChange(e.target.value)}
            onBlur={handlePriceBlur}
            sx={{ maxWidth: 200 }}
            helperText="Sales price in USD (max 2 decimal places)."
          />
        </Stack>
      </CardContent>
    </Card>
  );
};

export default ProductBasicInfoSection;
